import { ISalesOrderResponseProduct } from '~/interfaces/get-sales-order';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import {
  DELIVERY_DESCRIPTION,
  DONATION_DELIVERY_PRODUCT_TYPE,
  STANDARD_PRODUCT_TYPE,
  PURCHASE_CHANNEL_STORE,
  NATIONAL_SALES_CHAT_STORE_ID,
  DONATION_IMG,
  DELIVERY_IMG,
  FALLBACK_IMG,
  DONATION_SEGMENT,
  DELIVERY_SEGMENT,
  WARRANTY_SEGMENT,
} from '~/constant/constants';
import { IAddressFields } from '~/interfaces/address-fields';
import { TYPES } from '~/modules/payment/constants';
import type { FeatureFlag, Maybe } from '~/gql/__generated__/types';

export function getProtocol(url: string) {
  return url?.startsWith('localhost') || url?.startsWith('127.0.0.1')
    ? 'http://'
    : 'https://';
}

export function splitText(text: string | undefined, splitFactor: string) {
  if (!text) return;
  return text.split(splitFactor);
}

export function concatTextWithFactor(text1: string, text2: string) {
  return text1 + '##' + text2;
}

export function formatCurrency(amountVal = 0) {
  return amountVal.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

export function hasArray(values: unknown) {
  return Array.isArray(values) && values.length ? values : undefined;
}

export function getNetPriceTotal(itemsArray: ISalesOrderResponseProduct[]) {
  return (
    hasArray(itemsArray)?.reduce((total = 0, { NetAmount }) => {
      const netAmount = parseFloat(NetAmount || '0');
      return total + (isNaN(netAmount) ? 0 : +netAmount.toFixed(2));
    }, 0) || 0
  );
}

export function getPriceTotal(itemsArray: ISalesOrderResponseProduct[]) {
  return (
    hasArray(itemsArray)?.reduce((total = 0, { Price, Qty }) => {
      const netAmount = parseFloat(Price || '0') * Qty;
      return total + (isNaN(netAmount) ? 0 : +netAmount.toFixed(2));
    }, 0) || 0
  );
}

export function getProductLines(orderLineItems: ISalesOrderResponseProduct[]) {
  return orderLineItems?.filter(
    (product: ISalesOrderResponseProduct) =>
      String(product.ProductType) === String(STANDARD_PRODUCT_TYPE),
  );
}

export function getNonProductLines(
  orderLineItems: ISalesOrderResponseProduct[],
) {
  return orderLineItems?.filter(
    (product: ISalesOrderResponseProduct) =>
      product.ProductType === DONATION_DELIVERY_PRODUCT_TYPE &&
      product.Description?.toLowerCase() !== DELIVERY_DESCRIPTION,
  );
}

export function getDeliveryLines(orderLineItems: ISalesOrderResponseProduct[]) {
  return orderLineItems?.filter(
    (product: ISalesOrderResponseProduct) =>
      product.ProductType === DONATION_DELIVERY_PRODUCT_TYPE &&
      product.Description?.toLowerCase() === DELIVERY_DESCRIPTION,
  );
}

export function getDeliverySetupLines(
  orderLineItems: ISalesOrderResponseProduct[],
) {
  return orderLineItems?.filter(
    (product: ISalesOrderResponseProduct) =>
      product.ProductType === DONATION_DELIVERY_PRODUCT_TYPE &&
      product.Segment?.toLowerCase() === DELIVERY_SEGMENT,
  );
}

export function getWarrantyLines(orderLineItems: ISalesOrderResponseProduct[]) {
  return orderLineItems?.filter(
    (product: ISalesOrderResponseProduct) =>
      product.ProductType === DONATION_DELIVERY_PRODUCT_TYPE &&
      product.Segment?.toLowerCase() === WARRANTY_SEGMENT,
  );
}

export function getDonationLines(orderLineItems: ISalesOrderResponseProduct[]) {
  return orderLineItems?.filter(
    (product: ISalesOrderResponseProduct) =>
      product.ProductType === DONATION_DELIVERY_PRODUCT_TYPE &&
      product.Segment?.toLowerCase() === DONATION_SEGMENT,
  );
}

export function formatPhoneNumber(phoneNumber: string) {
  if (!phoneNumber) return '';
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
}

interface PaymentStylesMap {
  [key: string]: string;
}

export const creditCardOverrideStyles = `body { overflow: hidden !important; }

#paymentForm .cvv-help {
  bottom: 43px !important;
}
`;
export const adyenOverrideStyles = `body {  overflow: hidden !important; background-color: transparent !important; }

/* Remove grey backgrounds from Adyen elements */
#checkout-container,
#adyen-dropin,
.adyen-checkout__dropin,
.adyen-checkout__payment-method,
.adyen-checkout__payment-methods-list {
  background-color: transparent !important;
  background: none !important;
}

/* Remove padding from payment method details */
.adyen-checkout__payment-method__details {
  padding: 0 !important;
}

.adyen-checkout__payment-method--selected {
  background: transparent !important;
  border: none !important;
}

.adyen-checkout__payment-method--selected .adyen-checkout__payment-method__header {
    display: none !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
}

/* Labels */
.adyen-checkout__label {
  font-family: 'Rubik', sans-serif !important;
  font-size: 12px !important;
  font-weight: normal !important;
  line-height: 12px !important;
  color: #1a1a1a !important;
}

.adyen-checkout__label__text {
  font-family: 'Rubik', sans-serif !important;
  font-size: 12px !important;
  font-weight: normal !important;
  line-height: 12px !important;
  color: #1a1a1a !important;
}

/* Remove blue border on input focus */
.adyen-checkout__input,
.adyen-checkout__input-wrapper,
.adyen-checkout__input--focus,
.adyen-checkout__input-wrapper--focus,
.adyen-checkout__field--focused .adyen-checkout__input,
.adyen-checkout__field--focused .adyen-checkout__input-wrapper,
.adyen-checkout__field:focus-within .adyen-checkout__input,
.adyen-checkout__field:focus-within .adyen-checkout__input-wrapper {
  border-color: #b9c4c9 !important;
  outline: none !important;
  box-shadow: none !important;
}

/* Error messages */
.adyen-checkout__error-text {
  font-family: 'Rubik', sans-serif !important;
  font-size: 12px !important;
  font-weight: normal !important;
  line-height: 12px !important;
  color: #ff4d4f !important;
}

/* Error state label */
.adyen-checkout__field--error .adyen-checkout__label {
  color: #ff4d4f !important;
}
`;

export const PaymentStylesMap: PaymentStylesMap = {
  Visa: creditCardOverrideStyles,
  VI: adyenOverrideStyles,
};
export const getHostName = (url: string = ''): string => {
  return (
    url.includes('//') ? url.split('/')[2] : url.split('/')[0]
  ).toLowerCase();
};

export function isPurchaseChannelStore(purchaseChannel = '') {
  if (!purchaseChannel) return false;
  return PURCHASE_CHANNEL_STORE === purchaseChannel.toLowerCase();
}

export function extractCardType(cardName: string = 'Credit card'): string {
  switch (cardName.toLowerCase()) {
    case 'vi':
      return 'VISA';
    case 'mc':
      return 'Mastercard';
    case 'ds':
      return 'Discover';
    case 'amex':
      return 'American Express';
    case TYPES?.SYNCHRONY?.toLowerCase():
      return 'Synchrony';
    case TYPES?.PROGRESIVE?.toLowerCase():
      return 'Progressive';
    case TYPES?.AFFIRM?.toLowerCase():
      return 'Affirm';
    default:
      return 'Credit card';
  }
}

export function isNationalSalesChat(storeId: string) {
  return (storeId && NATIONAL_SALES_CHAT_STORE_ID.includes(storeId)) || false;
}

export function getItemImage(lineItem: ISalesOrderResponseProduct): string {
  if (lineItem.ProductType === DONATION_DELIVERY_PRODUCT_TYPE) {
    if (lineItem.Segment?.toLowerCase() === DELIVERY_SEGMENT)
      return DELIVERY_IMG;
    if (lineItem.Segment?.toLowerCase() === DONATION_SEGMENT)
      return DONATION_IMG;
  }
  return lineItem.ProductImageUrl || FALLBACK_IMG;
}

export function getFnameLname(name: string) {
  const fullName = name || '';
  let firstName = '';
  let lastName = '';

  if (fullName) {
    const nameParts = fullName.trim().split(' ');
    firstName = nameParts[0];
    lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
  }
  return { firstName, lastName };
}

export function getShippingAddressFromOrder(
  orderDetails: ISalesOrderResponse | undefined,
): IAddressFields {
  if (!orderDetails) {
    return {
      firstName: '',
      lastName: '',
      emailAddress: '',
      phoneNumber: '',
      address: '',
      apartment: '',
      zipcode: '',
      city: '',
      state: '',
    };
  }
  const { firstName, lastName } = getFnameLname(orderDetails.Name);
  return {
    firstName: firstName,
    lastName: lastName,
    emailAddress: orderDetails?.Email || '',
    phoneNumber: orderDetails?.Phone || '',
    address: orderDetails?.Street || '',
    apartment: orderDetails?.StreetNumber || '',
    zipcode: orderDetails?.ZipCode || '',
    city: orderDetails?.City || '',
    state: orderDetails?.ISOStateCode || '',
  };
}

export const generateCSId = (
  prefix: string,
  name?: string | null,
  suffix?: string | number | null,
) =>
  `${prefix}-${(name ?? '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()}${suffix != null ? `-${suffix}` : ''}`;

export const generateProductUrl = (
  product: ISalesOrderResponseProduct,
): string => {
  const productSlug = product.Description.toLowerCase()
    .replace(/"/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

  return product.ProductRecordId && product.VariantId
    ? `https://${process.env.CURRENT_DOMAIN}/${productSlug}/${product.ProductRecordId}.p?variantid=${product.ProductRecordId}`
    : '';
};
export function normalizeEmail(email?: string | null): string {
  if (typeof email !== 'string') return '';
  const trimmedEmail = email.trim();
  if (!trimmedEmail) return '';
  return trimmedEmail.toLowerCase();
}

export function isFeatureEnabled(
  featureFlags: Array<Maybe<FeatureFlag>>,
  featureName: string,
): boolean {
  return (
    featureFlags?.find((flag) => flag?.name === featureName)?.enabled || false
  );
}
